import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Link,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
  SvgIcon
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Visibility,
  VisibilityOff,
  HelpOutline,
  AccountBalanceWallet, // Generic wallet/payment icon
  AccountBalance,     // Bank icon
  Transform,          // Transfer icon
  Payment              // Generic payment method icon
} from '@mui/icons-material';

// A placeholder icon for USSD, Barter, PayAttitude - replace with actuals if available
const UssdIcon = () => (
  <SvgIcon>
    <path d="M6 18c-1.1 0-2-.9-2-2V8c0-1.1.9-2 2-2h12c1.1 0 2 .9 2 2v8c0 1.1-.9 2-2 2H6zm2-8h8v2H8v-2zm0 3h8v2H8v-2z" /> {/* Example path */}
  </SvgIcon>
);
const BarterIcon = () => (
  <SvgIcon>
    <path d="M14 8H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-4 8H6v-2h4v2zm0-3H6V9h4v4zm6-5h-2V3h-2v2H8l4 4 4-4zM20 5V3h-2v2h-2v2h2v2h2V7h2V5h-2z" /> {/* Example path */}
  </SvgIcon>
);
const PayAttitudeIcon = () => (
   <SvgIcon>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" /> {/* Example path */}
  </SvgIcon>
);


const styles = {
  modalStyle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: '70%', md: '500px' }, // Responsive width
    maxWidth: '500px',
    bgcolor: 'background.paper',
    borderRadius: '12px',
    boxShadow: 24,
    p: 0, // No padding on the Box, DialogContent will handle it
    outline: 'none',
  },
  headerIconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#e8f5e9', // Light green background
    borderRadius: '50%',
    width: 60,
    height: 60,
    margin: '0 auto',
    marginTop: 3, // theme.spacing(3)
  },
  headerIcon: {
    color: '#4caf50', // Green icon
    fontSize: '2rem',
  },
  paymentTitle: {
    textAlign: 'center',
    my: 1, // margin top and bottom
    fontWeight: 'bold',
    color: 'text.secondary',
  },
  amountSection: {
    backgroundColor: '#fff3e0', // Light orange background
    padding: 2, // theme.spacing(2)
    borderRadius: '8px',
    textAlign: 'center',
    mx: 3, // Horizontal margin
    mb: 2,
  },
  amountText: {
    fontWeight: 'bold',
    fontSize: '1.75rem',
    color: '#333',
  },
  emailText: {
    fontSize: '0.9rem',
    color: 'text.secondary',
  },
  formContainer: {
    px: 3, // Horizontal padding
    pb: 2,
  },
  payButton: {
    backgroundColor: '#ff9800', // Orange button
    color: 'white',
    '&:hover': {
      backgroundColor: '#f57c00',
    },
    py: 2,
    fontSize: '1rem',
    fontWeight: 'bold',
    mt: 2,
    mb: 3,
  },
  alternativePaymentSectionTitle: {
    my: 1,
    mx: 3,
    fontWeight: 'bold',
    color: 'text.secondary',
    fontSize: '0.9rem'
  },
  alternativePaymentListItem: {
    py: 1,
    '&:hover': {
      backgroundColor: 'action.hover'
    }
  },
  alternativePaymentIcon: {
    minWidth: 35, // Adjust as needed
    color: 'text.secondary'
  },
  betaChip: {
    marginLeft: 'auto',
    height: '20px',
    fontSize: '0.7rem'
  },
  cancelLink: {
    display: 'block',
    textAlign: 'center',
    my: 2,
    color: 'text.secondary',
    cursor: 'pointer',
    textTransform: 'none'
  },
  inputLabel: { // For better positioning if needed with TextField variant="standard"
    // transform: 'translate(0, -8px) scale(0.75)',
  }
};

const BankPaymentModal = ({
  open,
  onClose,
  paymentTitle = "my Payment Title", // Default title
  amount = 0.00,
  currency = "NGN",
  userEmail = sessionStorage.getItem("email") || "",
  onPay,
  hcProviderType,
  hcProvider,
  service,
  onCancelAppointment
}) => {
  const [showCvv, setShowCvv] = useState(false);
  const [rememberCard, setRememberCard] = useState(false);
  const handleClickShowCvv = () => setShowCvv((show) => !show);
  const handleMouseDownCvv = (event) => event.preventDefault();

  // Determine the payment title and amount based on hcProviderType
  let displayPaymentTitle = paymentTitle;
  let displayAmount = amount;
  let displayUserEmail = userEmail;

  if (hcProviderType === 'pharmacy' && service) {
    displayPaymentTitle = `Payment for ${service.productName}`;
    displayAmount = service.sellingPrice;
    if (hcProvider && hcProvider.email) {
      displayUserEmail = hcProvider.email; // Use pharmacy email if available
    }
  }

  const formattedAmount = new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(displayAmount).replace('NGN', '').trim(); // Remove NGN if it's part of format, add manually

  const alternativePaymentMethods = [
    { text: 'Pay with USSD', icon: <UssdIcon />, beta: false },
    { text: 'Pay with Bank', icon: <AccountBalance />, beta: false },
    { text: 'Pay with Bank Transfer', icon: <Transform />, beta: false },
    { text: 'Pay with Barter', icon: <BarterIcon />, beta: false },
    { text: 'Pay with PayAttitude', icon: <PayAttitudeIcon />, beta: true },
  ];

  console.log(hcProvider)
  console.log(hcProviderType)
  console.log(service)


  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          width: { xs: '95%', sm: '480px' },
          margin: 2, // Ensure some margin on very small screens
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}> {/* Remove padding from DialogContent, manage internally */}
        <Box sx={styles.headerIconContainer}>
          <CreditCardIcon sx={styles.headerIcon} />
        </Box>

        <Typography variant="h6" sx={styles.paymentTitle}>
          {displayPaymentTitle}
        </Typography>

        <Box sx={styles.amountSection}>
          <Typography sx={styles.amountText}>
            {currency} {formattedAmount}
          </Typography>
          <Typography sx={styles.emailText}>
            {displayUserEmail}
          </Typography>
        </Box>

        <Box sx={styles.formContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="CARD NUMBER"
                variant="outlined"
                placeholder="0000 0000 0000 0000"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CreditCardIcon sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={7} sm={8}>
              <TextField
                fullWidth
                label="VALID TILL"
                variant="outlined"
                placeholder="MM / YY"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={5} sm={4}>
              <TextField
                fullWidth
                label="CVV"
                variant="outlined"
                placeholder="123"
                type={showCvv ? 'text' : 'password'}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="3 or 4 digit code on the back of your card">
                        <IconButton
                          aria-label="toggle CVV visibility"
                          onClick={handleClickShowCvv}
                          onMouseDown={handleMouseDownCvv}
                          edge="end"
                        >
                          <HelpOutline sx={{ fontSize: '1.2rem', color: 'text.disabled' }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Checkbox
                checked={rememberCard}
                onChange={(e) => setRememberCard(e.target.checked)}
                name="rememberCard"
                color="primary"
              />
            }
            label="Remember this card next time"
            sx={{ mt: 1, color: 'text.secondary' }}
          />

          <Button
            fullWidth
            variant="contained"
            sx={styles.payButton}
            onClick={onPay}
          >
            Pay {currency} {formattedAmount}
          </Button>

          {/* Skip Payment Button */}
          <Button
            fullWidth
            variant="outlined"
            sx={{
              mt: 1,
              py: 1.5,
              fontWeight: 'bold',
              fontSize: '1rem',
              textTransform: 'none',
              borderColor: '#4CAF50',
              color: '#4CAF50',
              '&:hover': {
                backgroundColor: '#f1f1f1',
                borderColor: '#45A049',
              },
            }}
            onClick={onPay} // Handle skip logic here
          >
            Skip Payment
          </Button>
        </Box>

        {/* Alternative Payment Methods */}
        <List dense sx={{px:1, py:0}}> {/* Dense list and less padding */}
          {alternativePaymentMethods.map((method, index) => (
            <ListItem key={index} button sx={styles.alternativePaymentListItem} dense>
              <ListItemIcon sx={styles.alternativePaymentIcon}>
                {method.icon}
              </ListItemIcon>
              <ListItemText primary={method.text} primaryTypographyProps={{fontSize: '0.95rem'}}/>
              {method.beta && <Chip label="Beta" color="info" sx={styles.betaChip} />}
            </ListItem>
          ))}
        </List>

        <Button
            fullWidth
            onClick={onClose}
            sx={styles.cancelLink}
        >
           {(hcProviderType === 'pharmacy' && service) ? "Cancel Payment" : "Cancel Appointment"}
        </Button>

      </DialogContent>
    </Dialog>
  );
};

export default BankPaymentModal;